<script setup lang="ts">

import {useNIRApplicationsStore} from "@/domains/home/services/nir/registration/stores";
import {onMounted, ref} from "vue";
import {dateTimeFormat} from "@/commons/composables/transformations.ts";
import {useInstitutionStore} from "@/domains/home/services/nir/associate-registers/stores";
import {BulkEntityApplication} from "@/domains/home/services/nir/registration/types";
import router from "@/router.ts";
import Pagination from "@/commons/components/Pagination.vue";

const loading = ref(false);
const page = ref(1)
const limit = ref(12)
const store = useNIRApplicationsStore()
const institutionStore = useInstitutionStore()

onMounted(() => {
  fetch()
  store.fetchBulkStatistics()
})

function fetch(){
  loading.value = true
  store.fetchBulkApplications(page.value, limit.value)
      .then(() =>{
        loading.value = false
      })
}

function open(data: BulkEntityApplication) {
  router.push({name: "nir-bulk-application-details", params: {id: data.id}})
}

function update(pg: number) {
  page.value = pg
  fetch()
}

</script>

<template>
  <div class="flex-1 flex flex-col p-2">
    <div class="grid grid-cols-4 gap-2 py-2" v-if="store.bulkStats">
      <div class="flex flex-col text-gray-500 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkStats.processing}}</label>
        <label class="text-sm">Processing</label>
      </div>
      <div class="flex flex-col text-success-600 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkStats.processed}}</label>
        <label class="text-sm">Processed</label>
      </div>
      <div class="flex flex-col text-danger-600 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkStats.failed}}</label>
        <label class="text-sm">Failed</label>
      </div>
      <div class="flex flex-col bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.bulkStats.total}}</label>
        <label class="text-sm">Total</label>
      </div>
    </div>
    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              #
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Tracking Number
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Associate Register
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Status
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Date
            </th>
            <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"></th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="(application, idx) in store.bulkApplications"
              :key="idx"
              class="hover:bg-gray-50 transition">
            <td class="py-3 px-4 border-b border-gray-200 font-bold">
              <span>{{ idx + 1 }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 font-semibold">
              <span>{{ application.trackingNumber }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span>{{ institutionStore.institutions?.find((i => i.id === application.institutionId))?.name || 'N/A' }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span class="text-xs font-medium px-3 py-1 rounded-full inline-block"
                    :class="{
                      'bg-gray-100 text-gray-600': application.status === 'PENDING',
                      'bg-primary-100 text-primary-800': application.status === 'PROCESSING',
                      'bg-success-100 text-success-800': application.status === 'PROCESSED',
                      'bg-danger-100 text-danger-800': application.status === 'FAILED'
                    }">
                {{ application.status.toUpperCase() }}
              </span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
              <span>{{ dateTimeFormat(application.createdAt) }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
              <div class="flex gap-2">
                <button class="btn-sm-primary" @click="open(application)"> <i class="fa fa-eye"></i> View</button>
              </div>
            </td>
          </tr>
          <tr v-if="store.bulkApplications && store.bulkApplications.length === 0">
            <td colspan="8" class="py-8 text-center text-gray-500">
              No applications submitted
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <Pagination
          :pagination="store.pagination"
          :page="page"
          :limit="limit"
          label="applications"
          @update="update"
      />
    </div>
  </div>
</template>

<style scoped>

</style>